/**
 * Offline downloads — shared types.
 *
 * A DownloadRecord describes one lesson video saved to the device. File paths
 * are stored RELATIVE to the offline root (see paths.ts) because iOS moves the
 * app container between installs/updates and absolute paths go stale.
 */
import type { LessonPageData } from '@/supabase/services/lessonOptimized';

export type DownloadStatus = 'queued' | 'downloading' | 'paused' | 'complete' | 'failed';

export interface DownloadRecord {
  /** lesson_videos.id — the registry key. */
  lessonVideoId: number;
  lessonId: number;
  courseId: number | null;
  courseSlug: string;
  lessonSlug: string;
  videoSlug: string | null;
  videoTitle: string;
  lessonTitle: string;
  courseTitle: string;
  durationSeconds: number;
  rendition: string;
  bytesTotal: number | null;
  bytesDownloaded: number;
  status: DownloadStatus;
  error: string | null;
  /** Relative to the offline root, e.g. `videos/123.mp4`. */
  videoPath: string;
  /** Relative to the offline root, or null when the video has no captions. */
  captionsPath: string | null;
  /** Lesson thumbnail saved for the lock-screen Now Playing card, or null. */
  artworkPath?: string | null;
  /** Key of the lesson snapshot this video plays from (see snapshot.ts). */
  snapshotKey: string;
  /** Whether the Soundslice slice has notation (shown as a "needs internet" hint offline). */
  hasNotation: boolean | null;
  vimeoId: number | null;
  /** Owner — downloads are only playable by the user who made them. */
  userId: string;
  /** Background task id (react-native-background-downloader). */
  taskId: string;
  createdAt: string;
  completedAt: string | null;
  /** Last time the server confirmed this user may access the lesson. */
  lastVerifiedAt: string;
  /** False once a verify call denied access (subscription lapsed). */
  entitled: boolean;
}

export interface LessonSnapshot {
  key: string;
  savedAt: string;
  courseSlug: string;
  lessonSlug: string;
  data: LessonPageData;
}

export interface OfflineSourceResponse {
  video: {
    url: string;
    expires_at: string | null;
    rendition: string;
    width: number | null;
    height: number | null;
    bytes: number | null;
  };
  captions: { url: string; language: string; name: string | null } | null;
  vimeo_id: number;
  has_notation: boolean | null;
  verified_at: string;
}

export interface OfflineVerifyResponse {
  allowed: number[];
  denied: number[];
  verified_at: string;
}

export interface CaptionCue {
  start: number;
  end: number;
  text: string;
}

export type QueuedProgressEntry =
  | {
      type: 'left_off';
      userId: string;
      lessonVideoId: number;
      lessonId: number;
      seconds: number;
      at: string;
    }
  | {
      type: 'completed';
      userId: string;
      lessonVideoId: number;
      lessonId: number;
      completed: boolean;
      at: string;
    };
